import {createSlice, type PayloadAction} from "@reduxjs/toolkit";

import {buildAvmAddressParamFromClean, type GeoRefAddress, type ILatLonCoordinate} from "@reva/shared/geo-reference";
import type {RevaValuationPurpose} from "@reva/shared/valuation-purpose";

import type {ValuationAtAddressMatch} from "../api/payload-types/search-valuations-at-address";
import {buildApiQueryError, type QueryError} from "../util/errorUtil";
import type {ApiQueryError} from "./types";

/** Order/valuation address (declared address, possibly distinct from the calculated `address`). */
export interface OrderAddress {
    /** Street and House number */
    streetAndHouseNumber: string;
    /** City / Municipality */
    city: string;
    /** ZIP code / postcode */
    zipCode: string;
}

/** Address data of a loaded/created persistence. */
export interface PersistenceAddressData extends ILatLonCoordinate {
    /** original address query */
    addressQuery: string;
    /** address (either order address, or reference address) used for calculations and indications */
    address: GeoRefAddress;
    /** clean form of address query */
    addressClean: string;
    /** if true, the `address` property is a reference address */
    hasReferenceAddress: boolean;
    /** optional comment for the reference address */
    referenceAddressComment?: string | null;
    /** declared address of the valuation/order, can be the same as `address` if no reference address is used */
    orderAddress: OrderAddress;
    /** clean form of order address */
    orderAddressClean: string;
}

/** Multiple ambiguous addresses returned for a query. */
export interface AddressAmbiguousData {
    addresses: GeoRefAddress[];
}

/**
 * #18907: Existing valuations found at the address the user is about to create a new valuation for,
 * together with the resolved address that would be used for the new valuation (so the user can still
 * confirm the new one after seeing the duplicates).
 */
export interface DuplicateValuationsData {
    matches: ValuationAtAddressMatch[];
    /** The distinct address that will be used if the user decides to create a new valuation anyway. */
    pendingAddress: GeoRefAddress;
}

/** #19481: Ein Value-Order-Auftrag (EVA+-Sprung), der noch keine REVA-ID hat und nach der Zweckwahl importiert wird. */
export interface PendingOrderImport {
    orderId: string;
    clientId: string;
}

/**
 * #19481: Der User darf neue REVA-IDs mit mehreren Zwecken anlegen und muss wählen — bis dahin wartet entweder die
 * Adresse (Startseite, Gegenstück zu {@link DuplicateValuationsData}) oder der zu importierende Auftrag (EVA+-Sprung).
 */
export type PurposeChoiceData = {
    /** Die zur Wahl stehenden Zwecke. */
    options: RevaValuationPurpose[];
} & (
    | {
          /** Die Adresse, mit der nach der Wahl angelegt wird. */
          pendingAddress: GeoRefAddress;
          pendingOrder?: never;
      }
    | {
          /** Der Auftrag, der nach der Wahl importiert wird. */
          pendingOrder: PendingOrderImport;
          pendingAddress?: never;
      }
);

interface AddressState {
    initialized: boolean;
    loading: boolean;
    persistenceAddressData: PersistenceAddressData | null;
    addressAmbiguousData: AddressAmbiguousData | null;
    duplicateValuationsData: DuplicateValuationsData | null;
    purposeChoiceData: PurposeChoiceData | null;
    queryError: ApiQueryError | null;
}

const initialState: AddressState = {
    initialized: false,
    loading: false,
    persistenceAddressData: null,
    addressAmbiguousData: null,
    duplicateValuationsData: null,
    purposeChoiceData: null,
    queryError: null,
};

export const addressSlice = createSlice({
    name: "address",
    initialState,
    reducers: {
        clearAddress: (state) => {
            state.initialized = true;
            state.loading = false;
            state.persistenceAddressData = null;
            state.addressAmbiguousData = null;
            state.duplicateValuationsData = null;
            state.purposeChoiceData = null;
            state.queryError = null;
        },
        setLoading: (state) => {
            state.loading = true;
            state.addressAmbiguousData = null;
            state.duplicateValuationsData = null;
            state.purposeChoiceData = null;
            state.queryError = null;
        },
        setQueryError: (state, action: PayloadAction<QueryError>) => {
            state.initialized = true;
            state.loading = false;
            state.addressAmbiguousData = null;
            state.duplicateValuationsData = null;
            state.purposeChoiceData = null;
            state.queryError = buildApiQueryError(action.payload);
        },
        ambiguousAddressFound: (state, action: PayloadAction<{addresses: GeoRefAddress[]}>) => {
            const {addresses} = action.payload;
            state.initialized = true;
            state.loading = false;
            state.addressAmbiguousData = {addresses};
            state.duplicateValuationsData = null;
            state.purposeChoiceData = null;
            state.queryError = null;
        },
        removeQueryError: (state) => {
            state.queryError = null;
        },
        addressFromPersistenceLoaded: (state, action: PayloadAction<PersistenceAddressData>) => {
            const persistenceAddressData = action.payload;
            state.initialized = true;
            state.loading = false;
            state.persistenceAddressData = persistenceAddressData;
            state.addressAmbiguousData = null;
            state.duplicateValuationsData = null;
            state.purposeChoiceData = null;
            state.queryError = null;
        },
        /**
         * #18907: existing valuations were found at the address the user wanted to create a new
         * valuation for → open the duplicate-selection dialog (loading stops so the dialog can show).
         */
        duplicateValuationsFound: (state, action: PayloadAction<DuplicateValuationsData>) => {
            state.initialized = true;
            state.loading = false;
            state.addressAmbiguousData = null;
            state.duplicateValuationsData = action.payload;
            state.purposeChoiceData = null;
            state.queryError = null;
        },
        /** #18907: close the duplicate-selection dialog (user picked a row, created anyway, or cancelled). */
        clearDuplicateValuations: (state) => {
            state.duplicateValuationsData = null;
        },
        /** #19481: vor dem Anlegen den Zweck erfragen → Auswahldialog öffnen (Laden stoppt, damit er erscheint). */
        purposeChoiceRequired: (state, action: PayloadAction<PurposeChoiceData>) => {
            state.initialized = true;
            state.loading = false;
            state.addressAmbiguousData = null;
            state.duplicateValuationsData = null;
            state.purposeChoiceData = action.payload;
            state.queryError = null;
        },
        /** #19481: Auswahldialog schließen (Zweck gewählt oder abgebrochen). */
        clearPurposeChoice: (state) => {
            state.purposeChoiceData = null;
        },
    },
    selectors: {
        selectInitialized: (state) => state.initialized,
        selectLoading: (state) => state.loading,
        selectAddressData: (state) => state.persistenceAddressData,
        selectAddressSelected: (state) => !!state.persistenceAddressData,
        /** True if the currently loaded valuation itself uses a reference address. */
        selectHasReferenceAddress: (state) => state.persistenceAddressData?.hasReferenceAddress ?? false,
        selectAddressAmbiguousData: (state) => state.addressAmbiguousData,
        selectDuplicateValuationsData: (state) => state.duplicateValuationsData,
        selectPurposeChoiceData: (state) => state.purposeChoiceData,
        selectError: (state) => state.queryError,
        /**
         * Selector for the value, which should be sent to further API calls as address parameter.
         */
        selectAddressParam: (state): string | null => {
            if (!state.persistenceAddressData) return null;
            // Always use Lat/Lon, because it is not guaranteed that the address query yields a distinct address result
            return buildAvmAddressParamFromClean(
                state.persistenceAddressData.addressClean,
                state.persistenceAddressData,
            );
        },
    },
});

export const {
    clearAddress,
    setLoading,
    setQueryError,
    ambiguousAddressFound,
    removeQueryError,
    addressFromPersistenceLoaded,
    duplicateValuationsFound,
    clearDuplicateValuations,
    purposeChoiceRequired,
    clearPurposeChoice,
} = addressSlice.actions;

export const {
    selectInitialized,
    selectError,
    selectLoading,
    selectAddressSelected,
    selectAddressData,
    selectHasReferenceAddress,
    selectAddressAmbiguousData,
    selectDuplicateValuationsData,
    selectPurposeChoiceData,
    selectAddressParam,
} = addressSlice.selectors;

export default addressSlice.reducer;
